import React, { useState } from 'react'
import { Rocket, Users, Target, DollarSign, TrendingUp, Calendar, Shield, Zap, Globe, Code, Star, ArrowRight, CheckCircle, Play } from 'lucide-react'

interface Web3StartupProps {
  data: {
    projectName: string
    tagline: string
    description: string
    logo: string
    tokenSymbol: string
    tokenPrice: string
    tokenPriceChange: number
    marketCap: string
    totalSupply: string
    circulatingSupply: string
    tokenomics: {
      distribution: Array<{
        category: string
        percentage: number
        amount: string
        vesting: string
      }>
      utilities: Array<{
        title: string
        description: string
        icon: string
      }>
    }
    roadmap: Array<{
      phase: string
      title: string
      description: string
      status: 'completed' | 'in-progress' | 'upcoming'
      date: string
      features: string[]
    }>
    team: Array<{
      name: string
      role: string
      bio: string
      avatar: string
      socialLinks: {
        twitter: string
        linkedin: string
        github: string
      }
    }>
    partnerships: Array<{
      name: string
      logo: string
      description: string
      type: 'investor' | 'partner' | 'advisor'
    }>
    features: Array<{
      title: string
      description: string
      icon: string
      category: 'technology' | 'business' | 'community'
    }>
    whitepaper: {
      title: string
      description: string
      downloadUrl: string
      pages: number
    }
    community: {
      discord: number
      telegram: number
      twitter: number
      totalMembers: number
    }
    funding: {
      raised: string
      investors: number
      valuation: string
      round: string
    }
  }
}

export default function Web3Startup({ data }: Web3StartupProps) {
  const [selectedTab, setSelectedTab] = useState<'overview' | 'tokenomics' | 'roadmap' | 'team'>('overview')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-400/30'
      case 'in-progress': return 'bg-blue-500/20 text-blue-400 border-blue-400/30'
      case 'upcoming': return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />
      case 'in-progress': return <Play className="w-4 h-4" />
      case 'upcoming': return <Calendar className="w-4 h-4" />
      default: return <Calendar className="w-4 h-4" />
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'technology': return 'bg-blue-500/20 text-blue-400'
      case 'business': return 'bg-green-500/20 text-green-400'
      case 'community': return 'bg-purple-500/20 text-purple-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'investor': return 'bg-green-500/20 text-green-400'
      case 'partner': return 'bg-blue-500/20 text-blue-400'
      case 'advisor': return 'bg-purple-500/20 text-purple-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-cyan-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.projectName}
                </h1>
                <p className="text-blue-200 text-sm">Web3 Startup</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => setSelectedTab('overview')}
                className={`transition-colors ${selectedTab === 'overview' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Overview
              </button>
              <button
                onClick={() => setSelectedTab('tokenomics')}
                className={`transition-colors ${selectedTab === 'tokenomics' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Tokenomics
              </button>
              <button
                onClick={() => setSelectedTab('roadmap')}
                className={`transition-colors ${selectedTab === 'roadmap' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Roadmap
              </button>
              <button
                onClick={() => setSelectedTab('team')}
                className={`transition-colors ${selectedTab === 'team' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Team
              </button>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                Join Community
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {data.projectName}
            </h2>
            <p className="text-xl md:text-2xl text-blue-200 mb-8 max-w-3xl mx-auto">
              {data.tagline}
            </p>
            <p className="text-lg text-white/80 mb-12 max-w-4xl mx-auto leading-relaxed">
              {data.description}
            </p>
            
            {/* Token Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-2xl mx-auto mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-2xl font-bold text-white">{data.tokenSymbol}</span>
                <span className="text-3xl font-bold text-cyan-400">{data.tokenPrice}</span>
                <span className={`text-lg ${data.tokenPriceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.tokenPriceChange >= 0 ? '+' : ''}{data.tokenPriceChange}%
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm text-white/80">Market Cap</div>
                  <div className="font-bold text-white">{data.marketCap}</div>
                </div>
                <div>
                  <div className="text-sm text-white/80">Total Supply</div>
                  <div className="font-bold text-white">{data.totalSupply}</div>
                </div>
                <div>
                  <div className="text-sm text-white/80">Circulating</div>
                  <div className="font-bold text-white">{data.circulatingSupply}</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center">
                <Rocket className="w-5 h-5 mr-2" />
                Join Launch
              </button>
              <button className="border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center">
                <Globe className="w-5 h-5 mr-2" />
                Read Whitepaper
              </button>
            </div>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Funding Raised</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.funding.raised}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">Community</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.community.totalMembers.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Star className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Investors</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.funding.investors}</div>
            </div>
            
            <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 backdrop-blur-sm rounded-xl p-6 border border-cyan-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-6 h-6 text-cyan-400" />
                <span className="text-cyan-400 font-semibold">Valuation</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.funding.valuation}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Overview Tab */}
          {selectedTab === 'overview' && (
            <div>
              {/* Features */}
              <div className="mb-16">
                <h2 
                  className="text-3xl font-bold mb-8 text-center"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Why Choose {data.projectName}?
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {data.features.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <Zap className="w-6 h-6 text-blue-400" />
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(feature.category)}`}>
                          {feature.category.charAt(0).toUpperCase() + feature.category.slice(1)}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                      <p className="text-white/80">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partnerships */}
              <div className="mb-16">
                <h2 
                  className="text-3xl font-bold mb-8 text-center"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Partners & Investors
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.partnerships.map((partner, index) => (
                    <div
                      key={index}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{partner.name.charAt(0)}</span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTypeColor(partner.type)}`}>
                          {partner.type.charAt(0).toUpperCase() + partner.type.slice(1)}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{partner.name}</h3>
                      <p className="text-white/80 text-sm">{partner.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Whitepaper */}
              <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm rounded-xl p-8 border border-blue-400/30">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4">{data.whitepaper.title}</h3>
                    <p className="text-white/80 mb-4">{data.whitepaper.description}</p>
                    <div className="flex items-center gap-4 text-sm text-white/60">
                      <span className="flex items-center gap-2">
                        <Code className="w-4 h-4" />
                        {data.whitepaper.pages} pages
                      </span>
                      <span className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Technical Documentation
                      </span>
                    </div>
                  </div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Download Whitepaper
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tokenomics Tab */}
          {selectedTab === 'tokenomics' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Token Economics
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Distribution */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-6">Token Distribution</h3>
                  <div className="space-y-4">
                    {data.tokenomics.distribution.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div>
                          <div className="font-semibold text-white">{item.category}</div>
                          <div className="text-white/60 text-sm">{item.amount}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-cyan-400">{item.percentage}%</div>
                          <div className="text-white/60 text-sm">{item.vesting}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Utilities */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-6">Token Utilities</h3>
                  <div className="space-y-4">
                    {data.tokenomics.utilities.map((utility, index) => (
                      <div key={index} className="p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                            <Zap className="w-4 h-4 text-cyan-400" />
                          </div>
                          <h4 className="font-semibold text-white">{utility.title}</h4>
                        </div>
                        <p className="text-white/80 text-sm">{utility.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Roadmap Tab */}
          {selectedTab === 'roadmap' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Development Roadmap
              </h2>
              
              <div className="space-y-8">
                {data.roadmap.map((phase, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                          phase.status === 'completed' ? 'bg-green-500/20 border-green-400' :
                          phase.status === 'in-progress' ? 'bg-blue-500/20 border-blue-400' :
                          'bg-gray-500/20 border-gray-400'
                        }`}>
                          <span className="text-lg font-bold text-white">{index + 1}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-4">
                          <h3 className="text-2xl font-bold text-white">{phase.title}</h3>
                          <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(phase.status)}`}>
                            {getStatusIcon(phase.status)}
                            {phase.status.replace('-', ' ')}
                          </span>
                        </div>
                        
                        <p className="text-white/80 mb-4">{phase.description}</p>
                        
                        <div className="flex items-center gap-2 text-white/60 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{phase.date}</span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {phase.features.map((feature, featureIndex) => (
                            <span
                              key={featureIndex}
                              className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Tab */}
          {selectedTab === 'team' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Our Team
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {data.team.map((member, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-300"
                  >
                    <div className="text-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-10 h-10 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                      <p className="text-cyan-400 font-semibold mb-3">{member.role}</p>
                      <p className="text-white/80 text-sm mb-4">{member.bio}</p>
                      
                      <div className="flex justify-center gap-3">
                        {Object.entries(member.socialLinks).map(([platform, url]) => (
                          <a
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors"
                          >
                            <span className="text-xs text-white/80">{platform}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Discord</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Telegram</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">GitHub</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.projectName}. Building the future of Web3.
          </p>
        </div>
      </footer>
    </div>
  )
}
